// ═══════════════════════════════════════════════════════════════
// RollCall — Analytics
// Summary stats, charts, and breakdowns
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, SimpleGrid, Box, Progress, Divider,
} from '../../components/gab/GabComponents';
import {
  IconClock,
} from '@tabler/icons-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { RC_EVENT_TYPE_META } from '../../types';
import type { RCEventType } from '../../types';
import dayjs from 'dayjs';
import { primary } from '../../lib/colors';

const CHART_COLORS = [
  'var(--color-primary-700)', '#07963F', 'var(--color-error)', '#A67103', '#7c3aed',
  '#0891b2', '#db2777', '#65a30d', '#ea580c', '#6366f1',
];

function renderPieLabel(props: any) {
  return `${props.name} (${(props.percent * 100).toFixed(0)}%)`;
}

export default function Analytics() {
  const { loadIncidents } = useAuthStore();
  const { personnel, events, checkins, loadAll } = useRollCallStore();

  useEffect(() => { loadIncidents(); loadAll(); }, []);

  const eventsByType = useMemo(() => {
    const counts: Record<string, number> = {};
    events.forEach(e => { counts[e.eventType] = (counts[e.eventType] || 0) + 1; });
    return counts;
  }, [events]);

  const eventsByStatus = useMemo(() => {
    const open = events.filter(e => e.eventStatus === 'open').length;
    const closed = events.filter(e => e.eventStatus === 'closed').length;
    return { open, closed };
  }, [events]);

  const personnelByAgency = useMemo(() => {
    const counts: Record<string, number> = {};
    personnel.filter(p => p.status === 'active').forEach(p => {
      const a = p.agency || 'Unassigned';
      counts[a] = (counts[a] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [personnel]);

  const mealCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    events.filter(e => e.eventType === 'meal').forEach(e => {
      const type = e.mealType || 'other';
      const mealCheckins = checkins.filter(c => String(c.eventId) === String(e.id)).length;
      counts[type] = (counts[type] || 0) + mealCheckins;
    });
    return counts;
  }, [events, checkins]);

  const totalCheckins = checkins.length;
  const currentlyIn = checkins.filter(c => !c.checkOutTime).length;
  const activePersonnel = personnel.filter(p => p.status === 'active').length;

  // Chart data — Events by Type (pie)
  const eventTypePieData = useMemo(() => {
    return Object.entries(eventsByType).map(([type, count]) => {
      const meta = RC_EVENT_TYPE_META[type as RCEventType] || RC_EVENT_TYPE_META.custom;
      return { name: meta.label, value: count };
    });
  }, [eventsByType]);

  // Chart data — Personnel by Agency (bar)
  const agencyBarData = useMemo(() => {
    return personnelByAgency.map(([agency, count]) => ({
      name: agency.length > 15 ? agency.slice(0, 15) + '...' : agency,
      count,
    }));
  }, [personnelByAgency]);

  // Chart data — Check-ins over time (last 7 days)
  const checkinsByDay = useMemo(() => {
    const days: { name: string; checkins: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const day = dayjs().subtract(i, 'day');
      const dayStr = day.format('YYYY-MM-DD');
      const count = checkins.filter(c => {
        const cDay = c.checkInTime ? dayjs(c.checkInTime).format('YYYY-MM-DD') : '';
        return cDay === dayStr;
      }).length;
      days.push({ name: day.format('ddd'), checkins: count });
    }
    return days;
  }, [checkins]);

  // Check-in rate (checked out vs still in)
  const checkedOut = checkins.filter(c => c.checkOutTime).length;
  const checkoutRate = totalCheckins > 0 ? Math.round((checkedOut / totalCheckins) * 100) : 0;

  return (
    <div>
      <Title order={3} mb="md" c="black">Analytics</Title>

      {/* Top-level Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[700]}` }}>
          <Text size="xs" c="dimmed">Total Check-ins</Text>
          <Text fz={28} fw={700}>{totalCheckins}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #07963F' }}>
          <Text size="xs" c="dimmed">Currently In</Text>
          <Text fz={28} fw={700} c="#07963F">{currentlyIn}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[900]}` }}>
          <Text size="xs" c="dimmed">Total Events</Text>
          <Text fz={28} fw={700}>{events.length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #A67103' }}>
          <Text size="xs" c="dimmed">Active Personnel</Text>
          <Text fz={28} fw={700} c="#A67103">{activePersonnel}</Text>
        </Paper>
      </SimpleGrid>

      {/* Check-in Rate Bar */}
      <Paper p="md" radius="sm" withBorder mb="lg">
        <Group justify="space-between" mb={4}>
          <Group gap="xs">
            <IconClock size={14} />
            <Text size="sm" fw={600}>Check-out Completion Rate</Text>
          </Group>
          <Text size="sm" c="dimmed">{checkedOut} / {totalCheckins} checked out ({checkoutRate}%)</Text>
        </Group>
        <Progress value={checkoutRate} color={checkoutRate >= 80 ? 'green' : checkoutRate >= 50 ? 'yellow' : 'red'} size="md" radius="xl" />
      </Paper>

      {/* Charts Row 1 */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        {/* Check-ins Last 7 Days */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Check-ins — Last 7 Days</Text>
          {checkinsByDay.some(d => d.checkins > 0) ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={checkinsByDay} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <RechartsTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="checkins" fill="var(--color-primary-700)" radius={[4, 4, 0, 0]} name="Check-ins" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="xl">No check-ins in the last 7 days</Text>
          )}
        </Paper>

        {/* Events by Type Pie */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Events by Type</Text>
          {eventTypePieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={eventTypePieData} cx="50%" cy="50%" outerRadius={85} innerRadius={45}
                  dataKey="value" label={renderPieLabel} labelLine={{ strokeWidth: 1 }}>
                  {eventTypePieData.map((_, idx) => (
                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="xl">No events yet</Text>
          )}
        </Paper>
      </SimpleGrid>

      {/* Charts Row 2 */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        {/* Personnel by Agency Bar */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Personnel by Agency (Top 10)</Text>
          {agencyBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={agencyBarData} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <RechartsTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" fill="#07963F" radius={[0, 4, 4, 0]} name="Personnel" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="xl">No personnel yet</Text>
          )}
        </Paper>

        {/* Quick Stats */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Event Status & Meals</Text>
          <Stack gap="md">
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb="xs">Event Status</Text>
              <Group justify="space-between" mb={4}>
                <Badge color="green" variant="light" size="lg">Open: {eventsByStatus.open}</Badge>
                <Badge color="gray" variant="light" size="lg">Closed: {eventsByStatus.closed}</Badge>
              </Group>
              <Progress.Root size="lg" radius="xl">
                <Progress.Section value={events.length > 0 ? (eventsByStatus.open / events.length) * 100 : 0} color="green" />
                <Progress.Section value={events.length > 0 ? (eventsByStatus.closed / events.length) * 100 : 0} color="gray" />
              </Progress.Root>
            </Box>

            <Divider />

            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb="xs">Meal Counts</Text>
              {Object.entries(mealCounts).length > 0 ? (
                <Stack gap="xs">
                  {Object.entries(mealCounts).map(([type, count]) => (
                    <Group key={type} justify="space-between">
                      <Text size="sm" tt="capitalize">{type}</Text>
                      <Badge variant="light" size="lg">{count}</Badge>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed" ta="center" py="md">No meal events recorded</Text>
              )}
            </Box>
          </Stack>
        </Paper>
      </SimpleGrid>
    </div>
  );
}
